import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus,
  Search,
  Ticket,
  AlertTriangle,
  Clock,
  CheckCircle2,
  MoreHorizontal,
  Trash2,
  Eye,
  ShieldAlert,
  Timer,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

// ============================================
// Types
// ============================================

interface TicketItem {
  id: string
  externalId: string | null
  source: string
  clientId: string | null
  clientName: string | null
  subject: string
  description: string | null
  status: string
  priority: string
  category: string | null
  assigneeId: string | null
  assigneeName: string | null
  tags: string[]
  timeSpent: number | null
  slaDeadline: string | null
  slaMet: boolean | null
  resolvedAt: string | null
  closedAt: string | null
  metadata: Record<string, unknown> | null
  createdAt: string
  updatedAt: string
}

interface TicketStats {
  total: number
  open: number
  inProgress: number
  waiting: number
  resolved: number
  closed: number
  byPriority: Array<{ priority: string; count: number }>
  slaBreaches: number
  slaMet: number
  avgResolutionMinutes: number | null
}

// ============================================
// Helpers
// ============================================

function priorityBadge(priority: string) {
  switch (priority) {
    case 'critical':
      return <Badge className="bg-red-500/10 text-red-500 border-red-500/20">Critical</Badge>
    case 'high':
      return <Badge className="bg-orange-500/10 text-orange-500 border-orange-500/20">High</Badge>
    case 'medium':
      return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Medium</Badge>
    case 'low':
      return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Low</Badge>
    default:
      return <Badge variant="secondary">{priority}</Badge>
  }
}

function statusBadge(status: string) {
  switch (status) {
    case 'open':
      return <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">Open</Badge>
    case 'in_progress':
      return <Badge className="bg-purple-500/10 text-purple-500 border-purple-500/20">In Progress</Badge>
    case 'waiting':
      return <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">Waiting</Badge>
    case 'resolved':
      return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Resolved</Badge>
    case 'closed':
      return <Badge className="bg-gray-500/10 text-gray-500 border-gray-500/20">Closed</Badge>
    default:
      return <Badge variant="secondary">{status}</Badge>
  }
}

const statusLabels: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  waiting: 'Waiting',
  resolved: 'Resolved',
  closed: 'Closed',
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatDuration(minutes: number | null): string {
  if (minutes === null) return '-'
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (hours < 24) return `${hours}h ${mins}m`
  const days = Math.floor(hours / 24)
  const remainingHours = hours % 24
  return `${days}d ${remainingHours}h`
}

function isSlaBreached(ticket: TicketItem): boolean {
  if (!ticket.slaDeadline) return false
  if (ticket.status === 'resolved' || ticket.status === 'closed') return false
  return new Date(ticket.slaDeadline) < new Date()
}

// ============================================
// Create Ticket Dialog
// ============================================

function CreateTicketDialog() {
  const [open, setOpen] = useState(false)
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState('medium')
  const [status, setStatus] = useState('open')
  const [clientName, setClientName] = useState('')
  const [assigneeName, setAssigneeName] = useState('')
  const [category, setCategory] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/tickets', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      toast({ title: 'Ticket created', description: 'New ticket has been added' })
      setOpen(false)
      resetForm()
    },
    onError: () => toast({ title: 'Error', description: 'Failed to create ticket', variant: 'destructive' }),
  })

  function resetForm() {
    setSubject('')
    setDescription('')
    setPriority('medium')
    setStatus('open')
    setClientName('')
    setAssigneeName('')
    setCategory('')
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button><Plus className="mr-2 h-4 w-4" /> New Ticket</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Ticket</DialogTitle>
          <DialogDescription>Log a new support ticket.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
          <div className="space-y-2">
            <Label>Subject *</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Brief description of the issue" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Client Name</Label>
              <Input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Client Pty Ltd" />
            </div>
            <div className="space-y-2">
              <Label>Assignee</Label>
              <Input value={assigneeName} onChange={(e) => setAssigneeName(e.target.value)} placeholder="Tech name" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="waiting">Waiting</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Network, Hardware, Software" />
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Detailed description of the issue..." rows={4} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={() => createMutation.mutate({
              subject,
              description: description || undefined,
              priority,
              status,
              clientName: clientName || undefined,
              assigneeName: assigneeName || undefined,
              category: category || undefined,
              source: 'manual',
            })}
            disabled={!subject || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Create Ticket'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Ticket Detail Dialog
// ============================================

function TicketDetailDialog({ ticket, onClose }: { ticket: TicketItem; onClose: () => void }) {
  const [newStatus, setNewStatus] = useState(ticket.status)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const statusMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.patch(`/tickets/${ticket.id}/status`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      toast({ title: 'Status updated', description: `Ticket status changed to ${statusLabels[newStatus] || newStatus}` })
      onClose()
    },
    onError: () => toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' }),
  })

  const breached = isSlaBreached(ticket)

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="pr-8">{ticket.subject}</DialogTitle>
          <DialogDescription>{ticket.clientName || 'No client assigned'}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Status</p>
              <div className="mt-0.5">{statusBadge(ticket.status)}</div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Priority</p>
              <div className="mt-0.5">{priorityBadge(ticket.priority)}</div>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Assignee</p>
              <p className="text-sm font-medium">{ticket.assigneeName || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Category</p>
              <p className="text-sm font-medium">{ticket.category || '-'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Source</p>
              <p className="text-sm font-medium capitalize">{ticket.source}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Time Spent</p>
              <p className="text-sm font-medium">{formatDuration(ticket.timeSpent)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Created</p>
              <p className="text-sm font-medium">{formatDateTime(ticket.createdAt)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">SLA Deadline</p>
              <p className={`text-sm font-medium ${breached ? 'text-red-500' : ''}`}>
                {ticket.slaDeadline ? formatDateTime(ticket.slaDeadline) : '-'}
                {breached && ' (BREACHED)'}
              </p>
            </div>
            {ticket.resolvedAt && (
              <div>
                <p className="text-xs text-muted-foreground">Resolved At</p>
                <p className="text-sm font-medium">{formatDateTime(ticket.resolvedAt)}</p>
              </div>
            )}
            {ticket.slaMet !== null && (
              <div>
                <p className="text-xs text-muted-foreground">SLA Met</p>
                <p className={`text-sm font-medium ${ticket.slaMet ? 'text-green-500' : 'text-red-500'}`}>
                  {ticket.slaMet ? 'Yes' : 'No'}
                </p>
              </div>
            )}
          </div>

          {ticket.description && (
            <div>
              <p className="text-xs text-muted-foreground">Description</p>
              <p className="text-sm mt-1 whitespace-pre-wrap">{ticket.description}</p>
            </div>
          )}

          {ticket.tags.length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Tags</p>
              <div className="flex flex-wrap gap-1">
                {ticket.tags.map((tag) => (
                  <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Status Update */}
          <div className="space-y-3 border rounded-lg p-4">
            <p className="text-sm font-medium">Update Status</p>
            <div className="flex items-center gap-3">
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="flex-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="waiting">Waiting</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="sm"
                onClick={() => statusMutation.mutate({ status: newStatus })}
                disabled={newStatus === ticket.status || statusMutation.isPending}
              >
                {statusMutation.isPending ? 'Updating...' : 'Update'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ============================================
// Main Page
// ============================================

export function TicketsPage() {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [priorityFilter, setPriorityFilter] = useState<string>('all')
  const [selectedTicket, setSelectedTicket] = useState<TicketItem | null>(null)
  const queryClient = useQueryClient()
  const { toast } = useToast()

  // Fetch tickets
  const { data: ticketsData, isLoading } = useQuery({
    queryKey: ['tickets', statusFilter, priorityFilter],
    queryFn: () => apiClient.get<{ data: TicketItem[]; meta: unknown }>('/tickets', {
      ...(statusFilter !== 'all' && { status: statusFilter }),
      ...(priorityFilter !== 'all' && { priority: priorityFilter }),
      limit: 100,
    }),
  })

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['tickets', 'stats'],
    queryFn: () => apiClient.get<TicketStats>('/tickets/stats'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/tickets/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] })
      toast({ title: 'Deleted', description: 'Ticket has been deleted' })
    },
    onError: () => toast({ title: 'Error', description: 'Failed to delete ticket', variant: 'destructive' }),
  })

  const tickets = ticketsData?.data?.data || ticketsData?.data || []
  const stats = statsData?.data || null

  const filtered = Array.isArray(tickets)
    ? tickets.filter((t: TicketItem) =>
        t.subject.toLowerCase().includes(search.toLowerCase()) ||
        (t.clientName || '').toLowerCase().includes(search.toLowerCase()) ||
        (t.assigneeName || '').toLowerCase().includes(search.toLowerCase())
      )
    : []

  const highPriorityCount = stats?.byPriority
    ? stats.byPriority
        .filter((p) => p.priority === 'critical' || p.priority === 'high')
        .reduce((sum, p) => sum + p.count, 0)
    : 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Ticket Intelligence</h1>
          <p className="text-muted-foreground">Manage and monitor support tickets</p>
        </div>
        <CreateTicketDialog />
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Ticket className="h-4 w-4 text-blue-500" />
              <p className="text-xs text-muted-foreground">Open Tickets</p>
            </div>
            <div className="text-2xl font-bold mt-1">{stats?.open ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1">{stats?.inProgress ?? 0} in progress</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-purple-500" />
              <p className="text-xs text-muted-foreground">In Progress</p>
            </div>
            <div className="text-2xl font-bold text-purple-500 mt-1">{stats?.inProgress ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1">{stats?.waiting ?? 0} waiting</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-orange-500" />
              <p className="text-xs text-muted-foreground">High Priority</p>
            </div>
            <div className="text-2xl font-bold text-orange-500 mt-1">{highPriorityCount}</div>
            <p className="text-xs text-muted-foreground mt-1">Critical + High</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-red-500" />
              <p className="text-xs text-muted-foreground">SLA Breaches</p>
            </div>
            <div className="text-2xl font-bold text-red-500 mt-1">{stats?.slaBreaches ?? 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Avg resolution: {formatDuration(stats?.avgResolutionMinutes ?? null)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Stats Bar */}
      {stats && stats.total > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              Resolution Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Timer className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">Avg Resolution:</span>
                <span className="font-medium">{formatDuration(stats.avgResolutionMinutes)}</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                <span className="text-muted-foreground">SLA Met:</span>
                <span className="font-medium text-green-500">{stats.slaMet}</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldAlert className="h-3.5 w-3.5 text-red-500" />
                <span className="text-muted-foreground">SLA Breached:</span>
                <span className="font-medium text-red-500">{stats.slaBreaches}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Total:</span>
                <span className="font-medium">{stats.total}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search tickets..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="waiting">Waiting</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {isLoading ? (
        <Card>
          <CardContent className="p-6">
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex gap-4">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-5 w-24" />
                  <Skeleton className="h-5 w-20" />
                  <Skeleton className="h-5 w-20" />
                  <Skeleton className="h-5 w-32" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Ticket className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
            <p className="text-lg font-medium text-muted-foreground">No tickets found</p>
            <p className="text-sm text-muted-foreground">Create your first ticket to get started</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Subject</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead>Assignee</TableHead>
                <TableHead>SLA</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((ticket: TicketItem) => {
                const breached = isSlaBreached(ticket)
                return (
                  <TableRow key={ticket.id} className="cursor-pointer" onClick={() => setSelectedTicket(ticket)}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium max-w-[300px] truncate">{ticket.subject}</span>
                        {breached && <ShieldAlert className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{ticket.clientName || '-'}</TableCell>
                    <TableCell>{statusBadge(ticket.status)}</TableCell>
                    <TableCell>{priorityBadge(ticket.priority)}</TableCell>
                    <TableCell className="text-sm">{ticket.assigneeName || '-'}</TableCell>
                    <TableCell>
                      {ticket.slaDeadline ? (
                        <span className={`text-xs ${breached ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}>
                          {formatDate(ticket.slaDeadline)}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{formatDate(ticket.createdAt)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setSelectedTicket(ticket); }}>
                            <Eye className="mr-2 h-4 w-4" /> View Details
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(ticket.id); }}>
                            <Trash2 className="mr-2 h-4 w-4" /> Delete Ticket
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Ticket Detail Dialog */}
      {selectedTicket && (
        <TicketDetailDialog
          ticket={selectedTicket}
          onClose={() => setSelectedTicket(null)}
        />
      )}
    </div>
  )
}
